import { DocumentParameters, QiraMessageContentChunk, Source, SystemGroupData } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MessageService, MessageType, QiraSystemGroup } from '@app/services/message.service';
import { SourcesService } from '@app/services/sources.service';
import { DisplayVehicle, QiraSubject, SourceUrl } from '@app/services/types';
import { ItemBoxComponent } from '@app/components/item-box/item-box.component';
import { SourceLinkLogoComponent } from '../source-link/source-link-logo.component';
import { DocumentTypeOptions } from './smart-search-filter/smart-search-filter.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MarkdownService } from 'ngx-markdown';
import { Tag } from 'primeng/tag';

@Component({
  selector: 'app-smart-search',
  template: `
    @for (group of filteredGroups(); track group.id + $index) {
      <a [routerLink]="group.link?.path" [queryParams]="group.link?.params" class="">
        <app-item-box [isButton]="false" [showArrow]="'right'" class="mt-2 w-full">
          <div class="inline-block flex flex-wrap items-center gap-2">
            <span class="font-semibold uppercase">{{ group.name }}</span>
            <div class="inline-block flex items-center gap-2">
              <app-source-link-logo [provider]="group.provider" />
              @if (group.provider === 'mercedes' || group.provider === 'volkswagen') {
                <p-tag severity="info" icon="pi pi-verified" [value]="'chat.source.oeData' | transloco" />
              }
              @if (showCitedTag() && group.cited) {
                <p-tag severity="success" [value]="'chat.source.aiSource' | transloco" />
              }
            </div>
          </div>

          <p class="truncate lg:mr-8">{{ group.text }}</p>
        </app-item-box>
      </a>
    }
  `,
  imports: [CommonModule, RouterLink, ItemBoxComponent, SourceLinkLogoComponent, Tag, TranslocoPipe],
  standalone: true,
})
export class SmartSearchComponent {
  content = input<QiraMessageContentChunk[] | undefined>();
  tabId = input<string | undefined>();
  filter = input<{ provider: Source['provider'][]; subject: Source['subject'][] }>();
  maxVisibleItems = input<number>(100);
  showCitedTag = input<boolean>(false);
  vehicle = input<DisplayVehicle>();
  onFilter = output<QiraSystemGroup[]>();

  messageService = inject(MessageService);
  translocoService = inject(TranslocoService);
  markdownService = inject(MarkdownService);
  sourcesService = inject(SourcesService);

  messageGroups = signal<QiraSystemGroup[]>([]);
  filteredGroups = signal<QiraSystemGroup[]>([]);

  onInit = effect(async () => {
    const searchEntryLink: QiraSystemGroup[] = (
      await Promise.all(
        this.content()?.map(async (message: QiraMessageContentChunk) => {
          const messageType = this.messageService.getMessageType(message);
          if (messageType === 'document') {
            const text = await this._getText(message);
            return {
              id: message?.document?.id || Math.random().toString(36).substring(2, 10),
              type: MessageType.Document,
              subject: this.messageService.getMessageSubject(message),
              provider: message?.provider,
              name: this._getTitle(message),
              text: text,
              link: this._getDocumentLink(message, this.tabId()),
              cited: !!message?.document?.cited,
            };
          } else if (messageType === 'widget') {
            const widgetGroups = await this.messageService.getWidgetGroups(message);
            return widgetGroups.map((group) => ({
              ...group,
              name: this._getTitle(message),
              cited: !!message?.document?.cited,
              link:
                message?.widget?.id === 'guided-diagnosis'
                  ? this._getDiagnosisLink(message, this.tabId())
                  : this._getWiringDiagramLink(message, group.componentData as SystemGroupData, this.tabId()),
            }));
          }
          return [];
        }) || [],
      )
    )
      .flat()
      .filter(Boolean);

    const slicedFilteredGroups = this.maxVisibleItems() ? searchEntryLink.slice(0, this.maxVisibleItems()) : searchEntryLink;
    this.messageGroups.set(slicedFilteredGroups);
  });

  onFilterChange = effect(() => {
    const filter = this.filter() || { provider: [], subject: [] };
    const filteredGroups = this._filterGroups(this.messageGroups(), filter);
    const slicedFilteredGroups = this.maxVisibleItems() ? filteredGroups.slice(0, this.maxVisibleItems()) : filteredGroups;
    this.filteredGroups.set(slicedFilteredGroups);

    this.onFilter.emit(slicedFilteredGroups);
  });

  private _filterGroups(groups: QiraSystemGroup[], filter: { provider: Source['provider'][]; subject: Source['subject'][] }): QiraSystemGroup[] {
    if (!filter || (!filter?.provider.length && !filter?.subject.length)) {
      return groups;
    }

    const filteredGroups = groups.filter((group: QiraSystemGroup) => {
      const { provider, type, subject } = group;
      const { provider: providerFilters, subject: subjectFilters } = this.filter() || { provider: [], subject: [] };

      const nonDefaultSubjects = DocumentTypeOptions.filter((t) => t !== QiraSubject.Default);
      const isStandardSubject = subject && subject !== QiraSubject.Default && nonDefaultSubjects.includes(subject);

      const matchesSubject = subjectFilters.length === 0 || subjectFilters.includes(subject!);
      const matchesDefault = type === MessageType.Document && !isStandardSubject && subjectFilters.includes(QiraSubject.Default);
      const matchesProvider = providerFilters.length === 0 || (provider && providerFilters.includes(provider));

      return (matchesSubject || matchesDefault) && matchesProvider;
    });

    return filteredGroups;
  }

  private _getTitle(content: QiraMessageContentChunk | undefined): string {
    const messageType = this.messageService.getMessageType(content);
    if (messageType === MessageType.Document) {
      return this.translocoService.translate('source.category.' + content?.document?.subject);
    } else if (messageType === MessageType.Widget) {
      const widgetType = content?.widget?.id;
      return widgetType ? this.translocoService.translate('source.category.' + widgetType) : '';
    }
    return '';
  }

  private async _getText(content: QiraMessageContentChunk | undefined): Promise<string> {
    if (content?.document?.previewText) {
      const parsedHtml = await this.markdownService.parse(content.document.previewText);
      const doc = new DOMParser().parseFromString(parsedHtml || '', 'text/html');
      return doc.body.textContent || '';
    } else if (content?.widget?.id === 'guided-diagnosis') {
      return this.translocoService.translate('chatMessages.previewText.guided-diagnosis');
    } else if (content?.widget?.id === 'component-wiring-diagram') {
      return this.translocoService.translate('chatMessages.previewText.component-wiring-diagram');
    }
    return content?.text ?? '';
  }

  private _getDocumentLink(message: QiraMessageContentChunk | undefined, tabId: string | undefined): SourceUrl | undefined {
    const messageType = this.messageService.getMessageType(message);
    const parameters: (DocumentParameters & { tecDocPrototypes?: string | undefined }) | undefined = message?.document?.parameters;

    if (messageType !== MessageType.Document || !tabId || !parameters) return;

    const tecDocPrototypes = this.vehicle()?.tecDocPrototypes;
    const parametersWithTecDoc: DocumentParameters & { tecDocPrototypes?: string | undefined } = {
      ...parameters,
      ...(tecDocPrototypes && tecDocPrototypes?.length > 0 ? { tecDocPrototypes: tecDocPrototypes.join(',') } : {}),
    };
    return this.sourcesService.getDocumentLink(parametersWithTecDoc, tabId, message?.provider);
  }

  private _getDiagnosisLink(message: QiraMessageContentChunk | undefined, tabId: string | undefined): SourceUrl | undefined {
    const widgetType = message?.widget?.id;
    const widget = message?.widget;
    const carTypeId = widget?.parameters?.carTypeId;
    const errorCode = widget?.parameters?.errorCode;
    const componentIds = widget?.parameters?.components?.map((comp) => Number(comp.componentId)) ?? [];
    const provider = message?.provider;

    if (widgetType !== 'guided-diagnosis' || !carTypeId || !tabId) return;

    return this.messageService.getDiagnosisLink({
      carTypeId,
      errorCode,
      componentIds,
      tabId,
      provider,
      variantGroupId: undefined,
    });
  }

  private _getWiringDiagramLink(
    message: QiraMessageContentChunk | undefined,
    systemGroup: SystemGroupData | undefined,
    tabId: string | undefined,
  ): SourceUrl | undefined {
    const widgetType = message?.widget?.id;
    const widget = message?.widget;
    const carTypeId = widget?.parameters?.carTypeId;

    if (widgetType !== 'component-wiring-diagram' || !systemGroup || !carTypeId || !tabId) return;

    return this.messageService.getDiagnosisLink({
      carTypeId,
      tabId,
      componentIds: systemGroup.componentData?.map((comp: any) => Number(comp.componentId)) || [],
      systemGroups: widget?.parameters?.systemGroups || [],
      selectedSystemGroupName: systemGroup.systemGroupName,
      isExplorerMode: true,
    });
  }
}
